/**
 * This is a temporary view of the PDP page
 *
 * @module views/__core/Page/Page
 * @memberof -Common
 */
import React, { useEffect } from 'react';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Modules from '@ulta/core/components/Modules/Modules';
import { usePageScroll } from '@ulta/core/hooks/usePageScroll/usePageScroll';
import { useStorePersistAction } from '@ulta/core/hooks/useStorePersistAction/useStorePersistAction';
import { useUpdateGpcPreferences } from '@ulta/core/hooks/useUpdateGpcPreferences/useUpdateGpcPreferences';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useServerRequestContext } from '@ulta/core/providers/ServerRequestContextProvider/ServerRequestContextProvider';
import { useShoppingAgentContext } from '@ulta/core/providers/ShoppingAgentProvider/ShoppingAgentProvider';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
/**
 * Represents a CMS Page
 *
 * @method * @returns CMSPage
 */
export const Page = React.forwardRef( ( props, _ ) => {
  const {
    dataCapture,
    id: pageId,
    modules,
    notificationConfig = {},
    seo = {},
    web: { sdkAction, headerScripts = [], backgroundColor, isShoppingAgentSiteWideToggleEnabled } = {},
    meta = {},
    invokeAction,
    invokeMutation
  } = handleEmptyObjects( props );

  const { inflection } = useDeviceInflection();
  const isMobile = inflection.MOBILE;
  const { storePersistAction } = meta;
  const uniqueId = React.useId();
  const { setShoppingAgentSiteWideToggleEnabled }  = useShoppingAgentContext();

  // Used for Birthday notifications, if configured we will have an ID and will want to render the Notification component
  const isNotification = !!notificationConfig.id;

  usePageScroll();

  const serverRequestContext = useServerRequestContext();

  useStorePersistAction( {
    props,
    storePersistAction,
    invokeAction
  } );

  useUpdateGpcPreferences( { invokeMutation } );

  useEffect( ()=>{
    setShoppingAgentSiteWideToggleEnabled( isShoppingAgentSiteWideToggleEnabled );
  }, [isShoppingAgentSiteWideToggleEnabled, setShoppingAgentSiteWideToggleEnabled] );

  return (
    <div
      className='Page'
      style={ {
        backgroundColor
      } }
    >
      { sdkAction && !isServer() && (
        <AsyncComponent
          moduleName='Sdk'
          id='SDK'
          meta={ {
            initAction: sdkAction
          } }
          initAction={ props.web.sdkAction }
        />
      ) }

      { headerScripts.map( ( i, index ) => {
        const id = i.tagId || `${uniqueId}-${index}`;
        return (
          <AsyncComponent
            key={ `${id}:${index}` }
            { ...spreadProps( { ...i } ) }
            id={ id }
          />
        );
      } ) }

      { modules && <Modules modules={ modules } /> }
      { dataCapture &&
      <AsyncComponent
        // Ensure session data is resolved before triggering data capture events
        { ...spreadProps( {
          ...dataCapture,
          searchData: meta?.searchData,
          unresolvedData: [
            ...( dataCapture.unresolvedData || [] ),
            { service: 'Client', serviceDataPath: 'hasResolvedSessionData' }
          ]
        } ) }
        id={ pageId }
      /> }
      { seo.WebpageMetaData && <AsyncComponent { ...spreadProps( { ...seo.WebpageMetaData } ) } /> }
      { isNotification && (
        <AsyncComponent
          moduleName='Notifications'
          notificationConfig={ notificationConfig }
          id={ notificationConfig.id }
        />
      ) }
    </div>
  );
} );

/**
 * @const {string} PAGE_MODULE_NAME - Page module name
 */
export const PAGE_MODULE_NAME = 'Page';

Page.displayName = 'Page';

export default Page;
